import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Link, Navigate, useNavigate } from "react-router";
import { ApiError, get } from "../api/client";
import { useLoad, usePoll } from "../api/hooks";
import { SpotlightList } from "./Spotlight";
import type { SubmissionList } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import {
  betaGaps, betaRequest, firstName, gapsMessage, GAP_ID, SocialMediaFields, MinorsQuestion, needsTermsTick, newChoice, ScorecardFields, SentSoFar, Step, TermsBlock,
  type BetaChoice,
} from "../components/BetaIntake";
import { Bar } from "../components/ui";
import { BetaFull } from "../components/BetaFull";
import { VideoRangePicker } from "../components/VideoRangePicker";
import { YoutubeHelp } from "../components/YoutubeHelp";
import { fmtBytes, titleFromFilename } from "../lib/format";
import { useIsDesktop } from "../lib/device";
import { cancelUpload, loadSession, readDurationSeconds, sessionMatches, Uploader, type Progress, type UploadSession } from "../lib/upload";
import { MAX_SOURCE_SECONDS, MAX_VIDEO_SECONDS, selectedPart, videoFileProblem, videoTime } from "../lib/videoSelection";

/**
 * The Player Spotlight beta (owner, 2026-10-22; canvas in docs/design/mocks/beta-intake/): the whole app
 * for a participant is this questionnaire, the upload and the page after it (App.tsx sends every other
 * address here, and the API refuses its other routes). The beta takes one video per person
 * (BETA_VIDEO_LIMIT): once it is sent, the thank-you page is all a participant sees, unless the check
 * refuses the video, which gives the place back. An operator opens the same pages at /beta to see what a
 * participant sees; these questionnaire uploads also count toward the global programme cap.
 */

/** The public program name used by the questionnaire and confirmation page. */
const tabTitle = () => "Player Spotlight Program";

/** A participant's frame: the brand and one link, no app navigation. `bare`: no link (during the upload). */
export function BetaShell({ preview, bare, foot, children }: { preview: boolean; bare?: boolean; foot?: ReactNode; children: ReactNode }) {
  const { signOut } = useAuth();
  const nav = useNavigate();
  const footRef = useRef<HTMLDivElement | null>(null);
  const hasFoot = !!foot;
  // the phone's bar at the bottom: its height, for the bug button an operator's preview still has (Shell.tsx does the same)
  useLayoutEffect(() => {
    const el = footRef.current;
    if (!el) return;
    const root = document.documentElement;
    const set = () => root.style.setProperty("--foot-h", `${el.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty("--foot-h");
    };
  }, [hasFoot]);
  return (
    <div className="page">
      <header className="bq-top">
        <span className="brand">DEEP THIRD</span>
        {bare ? <span /> : preview ? (
          <Link className="r" to="/">Back to the app</Link>
        ) : (
          <button className="r" onClick={() => void signOut().then(() => nav("/signin", { replace: true }))} data-testid="bq-signout">Sign out</button>
        )}
      </header>
      {children}
      {foot ? <div className="foot" ref={footRef}>{foot}</div> : null}
    </div>
  );
}

function useTabTitle(title: string) {
  useEffect(() => {
    const before = document.title;
    document.title = title;
    return () => {
      document.title = before;
    };
  }, [title]);
}

/** The questionnaire, then the upload. */
export default function BetaQuestionnaire({ preview = false }: { preview?: boolean }) {
  const nav = useNavigate();
  const desktop = useIsDesktop();
  const { me, refresh } = useAuth();
  useTabTitle(tabTitle());
  const [file, setFile] = useState<File | null>(null);
  const [durationS, setDurationS] = useState<number | null>(null);
  const [checking, setChecking] = useState(false);
  const [starting, setStarting] = useState(false);
  const [rangeStart, setRangeStart] = useState("00:00:00");
  const [rangeEnd, setRangeEnd] = useState("04:00:00");
  const [rangeNeeded, setRangeNeeded] = useState(false);
  const [over, setOver] = useState(false);
  const [choice, setChoice] = useState<BetaChoice>(() => newChoice(me?.user.display_name ?? ""));
  const [asked, setAsked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [pending, setPending] = useState<UploadSession | null>(() => loadSession());
  // the interrupted upload could not resume: this file goes as a new one, and its answers are asked for
  const [fresh, setFresh] = useState(false);
  const uploader = useRef<Uploader | null>(null);
  const picked = useRef<File | null>(null);
  const submitting = useRef(false);
  const sent = useLoad(() => get<SubmissionList>("/v1/beta/submissions"), []);
  usePoll(sent.reload, 30_000, !progress);
  useEffect(() => () => uploader.current?.cancelLocal(), []);

  const items = sent.data?.items ?? [];
  // An upload that did not finish holds the participant's place; in the browser it started in, it resumes here.
  const unfinished = items[0]?.status === "uploading" ? items[0] : null;
  const done = !preview && sent.data?.videos_left === 0 && !unfinished;

  const fileProblem = file ? videoFileProblem(file) ?? (durationS !== null && durationS > MAX_SOURCE_SECONDS
    ? "This recording is longer than 8 hours. Shorten the source video to 8 hours or less, then choose up to 4 hours to analyse." : null) : null;
  const matchingPending = !fresh && !!pending && !!file && sessionMatches(pending, file);
  const showRange = !fileProblem && rangeNeeded;
  const part = showRange ? selectedPart(rangeStart, rangeEnd, durationS ?? (pending?.segment?.end_ms ?? MAX_SOURCE_SECONDS * 1000) / 1000) : { segment: null, problem: null };
  const resumable = matchingPending && sessionMatches(pending, file!, null, part.segment);
  const blocked = checking || starting || !!fileProblem || !!part.problem;
  const needsTerms = !resumable && needsTermsTick(me?.beta);
  const gaps = betaGaps(choice, !!file, !resumable, needsTerms);
  // marked only once Submit was pressed, and only while still missing
  const shown = asked ? gaps : [];
  const change = (c: BetaChoice) => {
    setError(null);
    setChoice(c);
  };
  const pick = (f: File | null) => {
    picked.current = f;
    setFile(f);
    setDurationS(null);
    setError(null);
    const saved = !fresh && f && pending && sessionMatches(pending, f) ? pending.segment : null;
    setRangeStart(videoTime((saved?.start_ms ?? 0) / 1000));
    setRangeEnd(videoTime((saved?.end_ms ?? MAX_VIDEO_SECONDS * 1000) / 1000));
    setRangeNeeded(!!saved);
    setChecking(!!f && !videoFileProblem(f));
    if (!f || videoFileProblem(f)) return;
    void readDurationSeconds(f).then((d) => {
      if (picked.current === f) {
        setDurationS(d);
        if (d !== null && d > MAX_VIDEO_SECONDS) setRangeNeeded(true);
        setChecking(false);
      }
    });
  };

  const submit = async () => {
    if (submitting.current || blocked) return;
    if (gaps.length) {
      setAsked(true);
      // the first answer missing may be out of sight: bring it up
      document.getElementById(GAP_ID[gaps[0]!])?.scrollIntoView({ block: "center", behavior: "smooth" });
      return;
    }
    if (!file) return;
    submitting.current = true;
    setStarting(true);
    setError(null);
    const duration = durationS ?? (part.segment ? part.segment.end_ms / 1000 : 60); // ingest measures the real duration
    const up = new Uploader(file, setProgress);
    uploader.current = up;
    setProgress({ sent: 0, total: file.size, parts_done: 0, parts_expected: 0 });
    try {
      // An older whole-file upload cannot be resumed with a newly chosen part.
      if (matchingPending && !resumable) {
        await cancelUpload(pending!);
        setFresh(true);
      }
      // Pitch length is not asked: "unknown", which the calibration treats as a full 22-yard pitch.
      await up.run({ pitch_length_choice: "unknown", duration_hint_s: duration, ...(part.segment ? { segment: part.segment } : {}), title: titleFromFilename(file.name), ...(resumable ? {} : { beta: betaRequest(choice, needsTerms) }) });
      void refresh(); // the yes to the terms is on record now: the next visit shows the day instead of the tick
      nav(preview ? "/beta/submitted" : "/submitted", { replace: true });
    } catch (e) {
      if ((e as Error).name === "AbortError") return;
      const active = loadSession();
      setPending(active);
      if (active) setFresh(false);
      setProgress(null);
      const code = e instanceof ApiError ? e.code : "";
      if (code === "TERMS_REQUIRED") {
        // the page thought the terms were agreed (another tab, a changed version): ask for the tick
        setChoice((c) => ({ ...c, terms: false }));
        setAsked(true);
        void refresh();
        return;
      }
      if (code === "STALE_TERMS") return setError("The Terms of use have a new version. Reload this page to read it and agree.");
      if (code === "BETA_PROGRAM_FULL") {
        if (sent.data) sent.set({ ...sent.data, capacity: { limit: 100, remaining: 0, full: true } });
        void sent.reload();
        return;
      }
      if (code === "ALLOWANCE_EXHAUSTED") {
        void sent.reload();
        return setError("The Player Spotlight beta takes one video per person, and you have already sent yours.");
      }
      if (resumable && code === "VALIDATION_FAILED") {
        setFresh(true);
        return setError("The interrupted upload could not resume. Answer the questions above to send this file again.");
      }
      if (code === "TOO_LONG") {
        document.getElementById(showRange ? "bq-range" : "bq-file")?.scrollIntoView({ block: "center", behavior: "smooth" });
        return setError("Choose a part up to 4 hours long to analyse.");
      }
      if (code === "TOO_LARGE") return setError("This video is larger than 50 GB. Choose a smaller file.");
      setError((e as Error).message);
    } finally {
      submitting.current = false;
      setStarting(false);
    }
  };
  const cancel = async () => {
    uploader.current?.cancelLocal();
    const s = loadSession();
    if (s) await cancelUpload(s).catch(() => undefined);
    setPending(null);
    setFresh(false);
    setProgress(null);
    void sent.reload();
  };

  if (!preview && !sent.data && !sent.error) return <BetaShell preview={preview}><div className="p tiny" style={{ paddingTop: 24 }}>Loading…</div></BetaShell>;
  if (done && !progress) return <Navigate to="/submitted" replace />;
  if (!sent.data && sent.error) return <BetaShell preview={preview}><div className="p center mt48"><p role="alert">We couldn’t check submission availability. Please try again.</p><button className="btn mt24" onClick={() => void sent.reload()}>Try again</button></div></BetaShell>;
  // An upload already holding a place can finish even after the last place is reserved.
  if (sent.data?.capacity.full && !unfinished && !progress) return <BetaShell preview={preview}><div className="p center bq-done"><BetaFull /></div></BetaShell>;

  if (progress && file) {
    const pct = progress.total ? Math.floor((progress.sent / progress.total) * 100) : 0;
    return (
      <BetaShell preview={preview} bare>
        <div className="p center bq-uploading" data-testid="uploading">
          <div className="huge" data-testid="upload-pct">{pct}%</div>
          <p className="sub mt10">{titleFromFilename(file.name)}<br />{fmtBytes(progress.sent)} of {fmtBytes(progress.total)}</p>
          <div className="mt32"><Bar value={pct / 100} /></div>
          <p className="tiny mt24">Keep this tab open until the upload finishes.</p>
          <div className="mt32"><button className="btn ghost wide" onClick={cancel}>Cancel upload</button></div>
        </div>
      </BetaShell>
    );
  }

  const alert = shown.length ? <p role="alert" className="err" data-testid="bq-alert">{gapsMessage(shown, choice)}</p> : null;
  const drop = (
    <div id="bq-file" className={`drop bq-drop${over ? " over" : ""}${shown.includes("file") || fileProblem ? " missing" : ""}`} data-testid="bq-drop"
      onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); const f = e.dataTransfer.files[0]; if (f) pick(f); }}>
      <div className="med">{file ? file.name : desktop ? "Drop a video here" : "Choose a video"}</div>
      <div className="tiny" role="status">{file ? `${fmtBytes(file.size)} · ${checking ? "Checking video…" : fileProblem ? "choose another video" : resumable ? "an interrupted upload of this file will resume" : showRange ? "select up to 4 hours below" : "ready to upload"}` : `${desktop ? "or choose a file · " : ""}MP4 or MOV, up to 4 h, 50 GB`}</div>
      <input type="file" accept="video/mp4,video/quicktime,.mp4,.mov" onChange={(e) => pick(e.target.files?.[0] ?? null)} data-testid="file-input" aria-label="Choose a video" aria-invalid={!!fileProblem} aria-describedby="bq-file-help" />
    </div>
  );
  const fileHelp = <p id="bq-file-help" className={`${fileProblem ? "err" : "tiny"} bq-hint`} role={fileProblem ? "alert" : undefined} data-testid="bq-file-help">{fileProblem ?? "Fixed camera, both stumps in view."}</p>;
  return (
    <BetaShell preview={preview}
      foot={!desktop && (
        <>
          {alert && <div style={{ marginBottom: 10 }}>{alert}</div>}
          <button className="btn" onClick={submit} disabled={blocked} data-testid="bq-submit">{checking ? "Checking video…" : "Submit"}</button>
        </>
      )}>
      <div className="bq" data-testid="beta-questionnaire">
        <p className="cap">Highlight Reel</p>
        <h1 className="bq-title" data-testid="bq-title">Player Spotlight Program</h1>
        <p className="sub bq-intro">Get a highlight reel of your play and insights into your game. Review your reel and share feedback on the analysis. If you choose, you can also approve it for a feature on Deep Third’s social media.</p>
        {preview && <p className="notice mt16" data-testid="bq-preview">Operator preview of what a participant sees. A video sent from here goes to your own account, with these answers.</p>}
        {pending && !file && <div className="notice mt16">An upload of <b>{pending.filename}</b> was interrupted. Choose the same file to resume it.</div>}
        {!preview && unfinished && !(pending && pending.video_id === unfinished.id) && (
          <div className="notice mt16" data-testid="bq-unfinished">Your upload of <b>{unfinished.title}</b> did not finish. To finish it, choose the same file in the browser you started it in; otherwise it is cleared after a day, and you can send your video again.</div>
        )}
        <Step n={1} title="Your video" testid="bq-step-video">
          {drop}
          {/* until a file is chosen, how to get it from YouTube Studio sits beside the hint (components/YoutubeHelp.tsx) */}
          {file ? fileHelp : <div className="bq-file-row">{fileHelp}<YoutubeHelp desktop={desktop} /></div>}
          {showRange && <VideoRangePicker durationS={durationS} start={rangeStart} end={rangeEnd} problem={part.problem} locked={resumable}
            onChange={(start, end) => { setRangeStart(start); setRangeEnd(end); setError(null); }} />}
          {!resumable && <MinorsQuestion choice={choice} onChange={change} gaps={shown} />}
        </Step>
        {resumable ? (
          <p className="tiny mt16" data-testid="bq-resuming">This upload continues where it stopped. Its answers went with it.</p>
        ) : (
          <>
            <Step n={2} title="Your scorecard" testid="bq-step-scorecard"><ScorecardFields choice={choice} onChange={change} gaps={shown} /></Step>
            <Step n={3} title="Social media permission" optional testid="bq-step-instagram"><SocialMediaFields choice={choice} onChange={change} gaps={shown} /></Step>
            <TermsBlock state={me?.beta} choice={choice} onChange={change} gaps={shown} />
          </>
        )}
        {error ? <p className="err mt16" role="alert" data-testid="bq-error">{error}</p> : null}
        {desktop && (
          <div className="bq-submit-row">
            {alert}
            <button className="btn wide" onClick={submit} disabled={blocked} data-testid="bq-submit">{checking ? "Checking video…" : "Submit"}</button>
          </div>
        )}
        <SentSoFar items={items} />
      </div>
    </BetaShell>
  );
}

/**
 * After the upload: what happens next, in the owner's words, and the video's state. No second video: the
 * beta takes one per person, so a participant whose place is free again (the check refused the video, the
 * upload was cancelled) goes back to the questionnaire.
 */
export function BetaSubmitted({ preview = false }: { preview?: boolean }) {
  const { me } = useAuth();
  const first = firstName(me?.user.display_name);
  useTabTitle(tabTitle());
  const sent = useLoad(() => get<SubmissionList>("/v1/beta/submissions"), []);
  const latest = sent.data?.items.slice(0, 1) ?? [];
  if (!preview && !sent.data && !sent.error) return <BetaShell preview={preview}><div className="p tiny" style={{ paddingTop: 24 }}>Loading…</div></BetaShell>;
  if (!preview && sent.data && (sent.data.videos_left !== 0 || latest[0]?.status === "uploading")) return <Navigate to="/" replace />;
  return (
    <BetaShell preview={preview}>
      <div className="p center bq-done" data-testid="bq-submitted">
        <svg className="bq-ok" viewBox="0 0 44 44" aria-hidden="true"><circle cx="22" cy="22" r="20.5" /><path d="M14 22.5l5.5 5.5L30.5 16.5" /></svg>
        <p className="cap mt24">Player Spotlight Program</p>
        <h1 className="mt10">Thanks{first ? `, ${first}` : ""}.</h1>
        <p className="sub mt16 bq-done-line" data-testid="bq-done-line">{latest[0]?.status === "expired" ? "Your storage period has ended. Your recording and Spotlight are no longer available." : "We will review your video and email you a private link to your edited reel. You can give feedback without allowing social media posting. We only post if you choose to approve the edit."}</p>
        {me && latest[0]?.status !== "expired" && <p className="tiny mt10" data-testid="bq-done-email">We'll write to {me.user.email}.</p>}
        <SpotlightList />
        <div className="bq-done-video"><SentSoFar items={latest} title="Your video" /></div>
      </div>
    </BetaShell>
  );
}
